// Shared HTTP client for the Sidetone web workspace (contract: docs/FRONTEND-INTEGRATION.md).
// request<T>() returns the complete JSON envelope ({data, meta} or {data, page, meta});
// it never unwraps data, never retries a mutation and never logs bodies or headers.
import type { AnalyticsAgents, AnalyticsInbound, AnalyticsLive, AnalyticsOutbound, AnalyticsSummary, AnalyticsUnanswered, CreateRingGroup, CreateRoutingRule, RingGroup, RoutingDecision, RoutingEvaluation, RoutingRule, UpdateRingGroup, UpdateRoutingRule } from '@bcio-voice/contracts';
import type { Voicemail, VoicemailGreeting, VoicemailQuery, VoicemailRuleSettings } from '@bcio-voice/contracts';
import type { CoachingCommand, CommandReceipt as CoachingReceipt } from '@bcio-voice/contracts';
import type { CreateIvrMenu, IvrMenu, QueueSettings, RoutingPrompt, UpdateIvrMenu, UpdateQueueSettings } from '@bcio-voice/contracts';

export interface ResponseMeta {
  request_id: string;
  event_cursor?: string;
}
export interface DataEnvelope<T> {
  data: T;
  meta: ResponseMeta;
}
export interface CollectionEnvelope<T> {
  data: T[];
  page: { next_cursor: string | null };
  meta: ResponseMeta;
}

export type ApiRequestOptions = RequestInit & { idempotencyKey?: string };

export interface ApiClientOptions {
  /** Defaults to "/api/v1". Relative request paths are appended to it. */
  baseUrl?: string;
  fetch?: typeof fetch;
  /** The current session's CSRF token; sent on every non-safe method. */
  getCsrfToken: () => string | null;
  /** Called once per 401 response so the shell can move to the expired-session state. */
  onUnauthenticated?: (error: ApiRequestError) => void;
  /** Called when the server rejects the CSRF token so the shell can refresh the session. */
  onCsrfRejected?: (error: ApiRequestError) => void;
  /**
   * Called on 403 `mfa_required` outside /auth/*: the session no longer satisfies the role's
   * policy, so a fresh sign-in with MFA is required (same handling as SSE `auth.policy_changed`).
   */
  onPolicyRejected?: (error: ApiRequestError) => void;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const IDEMPOTENCY_KEY = /^[a-zA-Z0-9_.:-]{8,128}$/;

/** A failed API call. Only server-provided safe fields are kept; nothing from the request. */
export class ApiRequestError extends Error {
  override readonly name = 'ApiRequestError';
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly retryable: boolean;
  readonly fields: Readonly<Record<string, string>> | null;
  /** A server's "ask again after" hint in milliseconds (the 503 coach_off_hours sends it), from the error object or,
   * failing that, its fields; null when absent. */
  readonly retryAfterMs: number | null;
  /** The ISO instant a refusal ends (coach_off_hours: when the coach is back), from the error object or its fields. */
  readonly until: string | null;
  /** True when the request may have reached the server but no response came back. */
  readonly outcomeUnknown: boolean;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    requestId?: string | null;
    retryable?: boolean;
    fields?: Record<string, string> | null;
    retryAfterMs?: number | null;
    until?: string | null;
    outcomeUnknown?: boolean;
  }) {
    super(init.message);
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.retryable = init.retryable ?? false;
    this.fields = init.fields ?? null;
    this.retryAfterMs = init.retryAfterMs ?? null;
    this.until = init.until ?? null;
    this.outcomeUnknown = init.outcomeUnknown ?? false;
  }
}

export function isApiRequestError(value: unknown): value is ApiRequestError {
  return value instanceof ApiRequestError;
}

export function isAbortError(value: unknown): boolean {
  return typeof value === 'object' && value !== null && (value as { name?: unknown }).name === 'AbortError';
}

/** A positive whole number of milliseconds, given as a number or (in fields) as digits; null otherwise. */
function retryAfterOf(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' && /^\d{1,10}$/.test(value) ? Number(value) : NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}
/** An ISO time a browser can read, at most 40 characters; null otherwise. */
function untilOf(value: unknown): string | null {
  return typeof value === 'string' && value.length <= 40 && Number.isFinite(Date.parse(value)) ? value : null;
}

function readErrorEnvelope(status: number, body: unknown): ApiRequestError {
  const error = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined;
  if (typeof error === 'object' && error !== null) {
    const e = error as Record<string, unknown>;
    const fields =
      typeof e.fields === 'object' && e.fields !== null
        ? Object.fromEntries(
            Object.entries(e.fields as Record<string, unknown>).filter(
              (entry): entry is [string, string] => typeof entry[1] === 'string',
            ),
          )
        : null;
    return new ApiRequestError({
      status,
      code: typeof e.code === 'string' ? e.code : `http_${status}`,
      message: typeof e.message === 'string' ? e.message : defaultMessage(status),
      requestId: typeof e.request_id === 'string' ? e.request_id : null,
      retryable: e.retryable === true,
      fields,
      retryAfterMs: retryAfterOf(e.retry_after_ms) ?? retryAfterOf(fields?.retry_after_ms),
      until: untilOf(e.until) ?? untilOf(fields?.until),
    });
  }
  return new ApiRequestError({ status, code: `http_${status}`, message: defaultMessage(status), retryable: status >= 500 });
}

function defaultMessage(status: number): string {
  if (status === 401) return 'Sign in to continue.';
  if (status === 403) return 'This action is not permitted.';
  if (status === 404) return 'Not found or not shared with you.';
  if (status === 429) return 'Too many requests. Wait a moment, then try again.';
  if (status >= 500) return 'The server could not complete the request.';
  return 'The request could not be completed.';
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.baseUrl = (options.baseUrl ?? '/api/v1').replace(/\/+$/, '');
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  }

  /** Builds the same-origin URL for a relative API path. Absolute URLs are refused. */
  url(path: string): string {
    if (/^[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith('//')) {
      throw new ApiRequestError({ status: 0, code: 'invalid_path', message: 'API paths must be relative.' });
    }
    return this.baseUrl + (path.startsWith('/') ? path : '/' + path);
  }

  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const { idempotencyKey, headers: suppliedHeaders, ...init } = options;
    const method = (init.method ?? 'GET').toUpperCase();
    const headers = new Headers(suppliedHeaders);
    headers.set('Accept', 'application/json');
    if (typeof init.body === 'string' && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const mutating = !SAFE_METHODS.has(method);
    if (mutating) {
      const csrf = this.options.getCsrfToken();
      if (csrf) headers.set('X-CSRF-Token', csrf);
    }
    if (idempotencyKey !== undefined) {
      if (!IDEMPOTENCY_KEY.test(idempotencyKey)) {
        throw new ApiRequestError({ status: 0, code: 'invalid_idempotency_key', message: 'The idempotency key is malformed.' });
      }
      headers.set('Idempotency-Key', idempotencyKey);
    }

    const url = this.url(path);
    let response: Response;
    try {
      response = await this.fetchImpl(url, { ...init, method, headers, credentials: 'same-origin', cache: 'no-store' });
    } catch (cause) {
      if (isAbortError(cause)) throw cause;
      throw new ApiRequestError({
        status: 0,
        code: 'network_error',
        message: mutating
          ? "The server didn't answer. The change may or may not have been applied."
          : "Couldn't reach the server. Check your connection, then try again.",
        retryable: !mutating,
        outcomeUnknown: mutating,
      });
    }

    if (response.status === 204 || response.status === 205) return undefined as T;
    let body: unknown = null;
    const text = await response.text();
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
    }
    if (!response.ok) {
      const error = readErrorEnvelope(response.status, body);
      if (response.status === 401) this.options.onUnauthenticated?.(error);
      if (response.status === 403 && error.code === 'csrf_invalid') this.options.onCsrfRejected?.(error);
      if (response.status === 403 && error.code === 'mfa_required' && !path.replace(/^\/+/, '').startsWith('auth/')) this.options.onPolicyRejected?.(error);
      throw error;
    }
    if (body === null) {
      throw new ApiRequestError({ status: response.status, code: 'invalid_response', message: 'The server sent an unreadable response.', retryable: !mutating });
    }
    return body as T;
  }

  /**
   * A same-origin request whose success body is not JSON (recording audio). Same credentials, CSRF
   * and 401/403 handling as `request`; a non-2xx response throws the parsed error envelope. The
   * caller owns the returned body. Never retried.
   */
  async open(path: string, options: RequestInit = {}): Promise<Response> {
    const method = (options.method ?? 'GET').toUpperCase();
    const headers = new Headers(options.headers);
    if (!headers.has('Accept')) headers.set('Accept', '*/*');
    const mutating = !SAFE_METHODS.has(method);
    if (mutating) {
      const csrf = this.options.getCsrfToken();
      if (csrf) headers.set('X-CSRF-Token', csrf);
    }
    let response: Response;
    try {
      response = await this.fetchImpl(this.url(path), { ...options, method, headers, credentials: 'same-origin', cache: 'no-store' });
    } catch (cause) {
      if (isAbortError(cause)) throw cause;
      throw new ApiRequestError({
        status: 0,
        code: 'network_error',
        message: mutating ? "The server didn't answer. The request may or may not have been recorded." : "Couldn't reach the server. Check your connection, then try again.",
        retryable: !mutating,
        outcomeUnknown: mutating,
      });
    }
    if (response.ok) return response;
    let body: unknown = null;
    try {
      body = JSON.parse(await response.text());
    } catch {
      body = null;
    }
    const error = readErrorEnvelope(response.status, body);
    if (response.status === 401) this.options.onUnauthenticated?.(error);
    if (response.status === 403 && error.code === 'csrf_invalid') this.options.onCsrfRejected?.(error);
    if (response.status === 403 && error.code === 'mfa_required') this.options.onPolicyRejected?.(error);
    throw error;
  }

  /** Convenience for JSON bodies; still returns the full envelope. */
  send<T>(method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, body?: unknown, extra: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...extra, method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  }
}

/** Builds a query string from defined, non-empty values. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

// ---- Messaging (FE-30) and notification preferences --------------------------------------------
// Seam: narrow local copies of the SMS contract (docs/implementation/OPUS-SMS-WEB.md) while the
// backend lane adds the schemas to packages/contracts. Swap these for the contract types once they
// land. Responses are still re-validated by features/messaging/model.ts before display.

export type MessagingCapabilityStatus = 'enabled' | 'disabled' | 'pending_registration';
export type MessagingReasonCode = 'messaging_disabled' | 'campaign_not_approved' | 'number_not_assigned' | 'no_messaging_permission';
export interface MessagingCapability {
  status: MessagingCapabilityStatus;
  allowed: boolean;
  reason_code: MessagingReasonCode | null;
  detail: string;
}

export type ThreadKind = 'customer' | 'system';
export type ConsentState = 'unknown' | 'pending_confirmation' | 'opted_in' | 'opted_out';
export interface MessageThread {
  id: string;
  number_id: string;
  remote_e164: string;
  contact_id: string | null;
  kind: ThreadKind;
  consent_state: ConsentState;
  last_message_at: string | null;
  last_message_preview: string;
  unread_count: number;
}

export type MessageDirection = 'inbound' | 'outbound';
export type MessageKind = 'conversation' | 'system_notification' | 'keyword_auto_reply';
export type MessageState = 'queued' | 'submitted' | 'sent' | 'delivered' | 'failed' | 'received' | 'suppressed';
export type MessageEncoding = 'gsm7' | 'ucs2';
export interface Message {
  id: string;
  thread_id: string;
  direction: MessageDirection;
  kind: MessageKind;
  body: string;
  segments: number;
  encoding: MessageEncoding;
  state: MessageState;
  error_code: string | null;
  error_detail: string | null;
  actor_user_id: string | null;
  template_id: string | null;
  created_at: string;
  submitted_at: string | null;
  delivered_at: string | null;
  provider_message_id: string | null;
}

export interface ConsentRecord {
  state: ConsentState;
  source: string | null;
  disclosure_version: string | null;
  updated_at: string | null;
}

export interface MessageTemplate {
  id: string;
  name: string;
  body: string;
  variables: string[];
  created_by: string | null;
  updated_at: string;
}

export interface RenderedTemplate {
  body: string;
  segments: number;
  encoding: MessageEncoding;
}

export interface QuietHours {
  start: string;
  end: string;
  timezone: string;
}
export interface NotificationPreferences {
  sms_enabled: boolean;
  sms_e164: string | null;
  sms_verified_at: string | null;
  categories: { call_missed: boolean; voicemail: boolean; system_alerts: boolean };
  quiet_hours: QuietHours | null;
  disclosure_version: string | null;
}

/** Thread and message lists put the cursor in `meta.next_cursor` (older lists use `page.next_cursor`). */
export interface CursorEnvelope<T> {
  data: T[];
  meta?: { next_cursor?: string | null; request_id?: string };
  page?: { next_cursor: string | null };
}

const seg = (id: string) => encodeURIComponent(id);

/** Typed calls for /messages/** and /me/notification-preferences/**. Never retries a send. */
export class MessagingClient {
  constructor(private readonly api: ApiClient) {}

  threads(params: { limit?: number; cursor?: string | null } = {}): Promise<CursorEnvelope<MessageThread>> {
    return this.api.request(`/messages/threads${query({ limit: params.limit ?? 50, cursor: params.cursor ?? null })}`);
  }

  messages(threadId: string, params: { limit?: number; cursor?: string | null } = {}): Promise<CursorEnvelope<Message>> {
    return this.api.request(`/messages/threads/${seg(threadId)}/messages${query({ limit: params.limit ?? 50, cursor: params.cursor ?? null })}`);
  }

  /** 202 {data: Message} in state `queued`. The key goes in the body (contract) and the header (convention). */
  send(threadId: string, body: string, idempotencyKey: string): Promise<DataEnvelope<Message>> {
    return this.api.send('POST', `/messages/threads/${seg(threadId)}/messages`, { body, idempotency_key: idempotencyKey }, { idempotencyKey });
  }

  /** Creates or reuses the thread for (number_id, remote_e164) and queues the first message. */
  startThread(input: { number_id: string; remote_e164: string; body: string }, idempotencyKey: string): Promise<DataEnvelope<Message>> {
    return this.api.send('POST', '/messages/threads', { ...input, idempotency_key: idempotencyKey }, { idempotencyKey });
  }

  consent(e164: string, signal?: AbortSignal): Promise<DataEnvelope<ConsentRecord> | ConsentRecord> {
    return this.api.request(`/messages/consents${query({ e164 })}`, signal ? { signal } : {});
  }

  templates(): Promise<CursorEnvelope<MessageTemplate>> {
    return this.api.request('/messages/templates');
  }

  renderTemplate(templateId: string, variables: Record<string, string>): Promise<DataEnvelope<RenderedTemplate> | RenderedTemplate> {
    return this.api.send('POST', `/messages/templates/${seg(templateId)}/render`, { variables });
  }

  preferences(): Promise<DataEnvelope<NotificationPreferences> | NotificationPreferences> {
    return this.api.request('/me/notification-preferences');
  }

  savePreferences(next: NotificationPreferences): Promise<DataEnvelope<NotificationPreferences> | NotificationPreferences> {
    return this.api.send('PUT', '/me/notification-preferences', next);
  }

  /** 202 when a code was queued; 409 `messaging_disabled` while texting can't be sent. */
  sendVerificationCode(): Promise<unknown> {
    return this.api.send('POST', '/me/notification-preferences/sms/verify', {});
  }

  /** 200 when the code matched; 422 `invalid_code` otherwise. */
  confirmVerificationCode(code: string): Promise<unknown> {
    return this.api.send('POST', '/me/notification-preferences/sms/confirm', { code });
  }
}

// ---- Inbound routing (migration 014; docs/implementation/OPUS-ROUTING.md "API") ----------------
// Contract types from packages/contracts. features/routing re-validates every response before display.

/** Typed calls for /routing/**. Routing CRUD takes no Idempotency-Key: edits carry expected_version. */
export class RoutingClient {
  constructor(private readonly api: ApiClient) {}

  ringGroups(signal?: AbortSignal): Promise<CollectionEnvelope<RingGroup>> {
    return this.api.request('/routing/ring-groups', signal ? { signal } : {});
  }

  ringGroup(id: string): Promise<DataEnvelope<RingGroup>> {
    return this.api.request(`/routing/ring-groups/${seg(id)}`);
  }

  /** 201; 409 `ring_group_name_conflict`; 422 `duplicate_member` or `unknown_member`. */
  createRingGroup(body: CreateRingGroup): Promise<DataEnvelope<RingGroup>> {
    return this.api.send('POST', '/routing/ring-groups', body);
  }

  /** 409 `version_conflict` when `expected_version` is stale. */
  updateRingGroup(id: string, body: UpdateRingGroup): Promise<DataEnvelope<RingGroup>> {
    return this.api.send('PATCH', `/routing/ring-groups/${seg(id)}`, body);
  }

  /** 204; 409 `ring_group_in_use` while a rule names the group, or `version_conflict`. */
  deleteRingGroup(id: string, expectedVersion: number): Promise<void> {
    return this.api.send('DELETE', `/routing/ring-groups/${seg(id)}${query({ expected_version: expectedVersion })}`);
  }

  rules(numberId: string | null, signal?: AbortSignal): Promise<CollectionEnvelope<RoutingRule>> {
    return this.api.request(`/routing/rules${query({ number_id: numberId })}`, signal ? { signal } : {});
  }

  rule(id: string): Promise<DataEnvelope<RoutingRule>> {
    return this.api.request(`/routing/rules/${seg(id)}`);
  }

  /** 201; 409 `routing_priority_conflict`; 422 with a schedule or target code. */
  createRule(body: CreateRoutingRule): Promise<DataEnvelope<RoutingRule>> {
    return this.api.send('POST', '/routing/rules', body);
  }

  updateRule(id: string, body: UpdateRoutingRule): Promise<DataEnvelope<RoutingRule>> {
    return this.api.send('PATCH', `/routing/rules/${seg(id)}`, body);
  }

  deleteRule(id: string, expectedVersion: number): Promise<void> {
    return this.api.send('DELETE', `/routing/rules/${seg(id)}${query({ expected_version: expectedVersion })}`);
  }

  /** Dry run at `at` (default now on the server). Nothing rings. */
  evaluate(ruleId: string, at: string | null): Promise<DataEnvelope<RoutingEvaluation>> {
    return this.api.send('POST', `/routing/rules/${seg(ruleId)}/evaluate`, at ? { at } : {});
  }

  /** Read with the call's own visibility; 404 when the call isn't visible. */
  decisions(callId: string): Promise<DataEnvelope<RoutingDecision>> {
    return this.api.request(`/routing/decisions${query({ call_id: callId })}`);
  }
}

// ---- Analytics (docs/implementation/OPUS-ANALYTICS.md) -----------------------------------------
// Contract types from packages/contracts. features/analytics re-validates every response before display.

export interface AnalyticsParams {
  from: string;
  to: string;
  timezone: string;
  team_id?: string | null;
  number_id?: string | null;
  user_id?: string | null;
}

export interface AnalyticsLiveParams {
  timezone?: string | null;
  team_id?: string | null;
  number_id?: string | null;
  user_id?: string | null;
}

const analyticsQuery = (p: AnalyticsParams) => query({ from: p.from, to: p.to, timezone: p.timezone, team_id: p.team_id, number_id: p.number_id, user_id: p.user_id });

/** Typed reads for /analytics/**. Every report is a GET; 403 without analytics access, 422 for an invalid window. */
export class AnalyticsClient {
  constructor(private readonly api: Pick<ApiClient, 'request' | 'url'>) {}

  summary(p: AnalyticsParams, signal?: AbortSignal): Promise<DataEnvelope<AnalyticsSummary>> {
    return this.api.request(`/analytics/summary${analyticsQuery(p)}`, signal ? { signal } : {});
  }

  inbound(p: AnalyticsParams, signal?: AbortSignal): Promise<DataEnvelope<AnalyticsInbound>> {
    return this.api.request(`/analytics/inbound${analyticsQuery(p)}`, signal ? { signal } : {});
  }

  outbound(p: AnalyticsParams, signal?: AbortSignal): Promise<DataEnvelope<AnalyticsOutbound>> {
    return this.api.request(`/analytics/outbound${analyticsQuery(p)}`, signal ? { signal } : {});
  }

  unanswered(p: AnalyticsParams, signal?: AbortSignal): Promise<DataEnvelope<AnalyticsUnanswered>> {
    return this.api.request(`/analytics/unanswered${analyticsQuery(p)}`, signal ? { signal } : {});
  }

  agents(p: AnalyticsParams, signal?: AbortSignal): Promise<DataEnvelope<AnalyticsAgents>> {
    return this.api.request(`/analytics/agents${analyticsQuery(p)}`, signal ? { signal } : {});
  }

  live(p: AnalyticsLiveParams, signal?: AbortSignal): Promise<DataEnvelope<AnalyticsLive>> {
    return this.api.request(`/analytics/live${query({ timezone: p.timezone, team_id: p.team_id, number_id: p.number_id, user_id: p.user_id })}`, signal ? { signal } : {});
  }

  /** Same-origin URL for the CSV download (cookie session, GET). 413 above 50,000 calls. */
  exportUrl(p: AnalyticsParams, options: { bom?: boolean } = {}): string {
    const base = analyticsQuery(p);
    return this.api.url(`/analytics/export.csv${base}${options.bom ? `${base ? '&' : '?'}bom=true` : ''}`);
  }
}

// ---- Voicemail (migration 015; docs/implementation/OPUS-VOICEMAIL.md section 2) -----------------------
// Contract types from packages/contracts. features/voicemail re-validates every response before display.

/** Typed calls for /voicemails/**, number greetings and rule voicemail settings. Never retries a change. */
export class VoicemailClient {
  constructor(private readonly api: ApiClient) {}

  /** Newest first; archived messages only with state=archived. `page.next_cursor` and `meta.next_cursor` match. */
  list(params: VoicemailQuery = {}, signal?: AbortSignal): Promise<CollectionEnvelope<Voicemail>> {
    return this.api.request(`/voicemails${query({ number_id: params.number_id, state: params.state, cursor: params.cursor, limit: params.limit })}`, signal ? { signal } : {});
  }

  get(id: string): Promise<DataEnvelope<Voicemail>> {
    return this.api.request(`/voicemails/${seg(id)}`);
  }

  /** Idempotent; 409 `voicemail_archived` or `version_conflict` (when `expectedVersion` is stale). */
  markListened(id: string, expectedVersion: number | null): Promise<DataEnvelope<Voicemail>> {
    return this.api.send('POST', `/voicemails/${seg(id)}/listened`, expectedVersion === null ? {} : { expected_version: expectedVersion });
  }

  /** Soft delete: the message moves to `archived`. 204, also when it already was. */
  archive(id: string): Promise<void> {
    return this.api.send('DELETE', `/voicemails/${seg(id)}`);
  }

  /** The playback hand-off: 303 to /recordings/{recording_id}/media, which applies its own authorization. */
  recordingPath(id: string): string {
    return `/voicemails/${seg(id)}/recording`;
  }

  /** The number's active greeting; 404 when it has none. */
  greeting(numberId: string, signal?: AbortSignal): Promise<DataEnvelope<VoicemailGreeting>> {
    return this.api.request(`/routing/numbers/${seg(numberId)}/voicemail-greeting`, signal ? { signal } : {});
  }

  /** Raw canonical WAV (201; replaces the active greeting). 413, 415 and 422 carry the server's reason. */
  uploadGreeting(numberId: string, wav: Uint8Array<ArrayBuffer>): Promise<DataEnvelope<VoicemailGreeting>> {
    return this.api.request(`/routing/numbers/${seg(numberId)}/voicemail-greeting`, { method: 'PUT', body: wav, headers: { 'Content-Type': 'audio/wav' } });
  }

  deleteGreeting(numberId: string): Promise<void> {
    return this.api.send('DELETE', `/routing/numbers/${seg(numberId)}/voicemail-greeting`);
  }

  ruleVoicemail(ruleId: string, signal?: AbortSignal): Promise<DataEnvelope<VoicemailRuleSettings>> {
    return this.api.request(`/routing/rules/${seg(ruleId)}/voicemail`, signal ? { signal } : {});
  }

  /** Bumps the rule's version (and the number's routing version); 409 `version_conflict` when stale. */
  saveRuleVoicemail(ruleId: string, voicemailOn: VoicemailRuleSettings['voicemail_on'], expectedVersion: number): Promise<DataEnvelope<VoicemailRuleSettings>> {
    return this.api.send('PUT', `/routing/rules/${seg(ruleId)}/voicemail`, { voicemail_on: voicemailOn, expected_version: expectedVersion });
  }
}

/** Live coaching (COACH-01..04): POST /calls/{id}/coaching queues one durable command. Refusals carry fixed codes
  * (coach_already_present, coach_busy_elsewhere, coach_device_unavailable, call_not_coachable, coach_self, ...). */
export class CoachingClient {
  constructor(private readonly api: ApiClient) {}

  command(callId: string, body: CoachingCommand, idempotencyKey: string): Promise<DataEnvelope<CoachingReceipt>> {
    return this.api.send('POST', `/calls/${seg(callId)}/coaching`, body, { idempotencyKey });
  }
}

// ---- Queues, phone menus and prompts (migration 016; docs/implementation/OPUS-IVR-QUEUES.md section 2) ----------
// Contract types from packages/contracts. features/flow re-validates every response before display.

/** Typed calls for /routing/prompts, ring group queue settings and /routing/ivr-menus. Never retries a change. */
export class FlowClient {
  constructor(private readonly api: ApiClient) {}

  prompts(signal?: AbortSignal): Promise<CollectionEnvelope<RoutingPrompt>> {
    return this.api.request('/routing/prompts', signal ? { signal } : {});
  }

  /** Raw canonical WAV (201). 409 `prompt_name_conflict`; 413, 415 and 422 carry the server's reason. */
  createPrompt(name: string, wav: Uint8Array<ArrayBuffer>): Promise<DataEnvelope<RoutingPrompt>> {
    return this.api.request(`/routing/prompts${query({ name })}`, { method: 'POST', body: wav, headers: { 'Content-Type': 'audio/wav' } });
  }

  /** New audio (and optionally a new name) under the same prompt id; 409 `version_conflict` when stale. */
  replacePrompt(id: string, wav: Uint8Array<ArrayBuffer>, expectedVersion: number, name: string | null): Promise<DataEnvelope<RoutingPrompt>> {
    return this.api.request(`/routing/prompts/${seg(id)}${query({ expected_version: expectedVersion, name })}`, { method: 'PUT', body: wav, headers: { 'Content-Type': 'audio/wav' } });
  }

  /** 204; 409 `prompt_in_use` while a menu or a queue uses it, or `version_conflict`. */
  deletePrompt(id: string, expectedVersion: number): Promise<void> {
    return this.api.send('DELETE', `/routing/prompts/${seg(id)}${query({ expected_version: expectedVersion })}`);
  }

  queue(ringGroupId: string, signal?: AbortSignal): Promise<DataEnvelope<QueueSettings>> {
    return this.api.request(`/routing/ring-groups/${seg(ringGroupId)}/queue`, signal ? { signal } : {});
  }

  /** Bumps the ring group's version; 409 `version_conflict` when `expected_version` is stale. */
  saveQueue(ringGroupId: string, body: UpdateQueueSettings): Promise<DataEnvelope<QueueSettings>> {
    return this.api.send('PUT', `/routing/ring-groups/${seg(ringGroupId)}/queue`, body);
  }

  menus(signal?: AbortSignal): Promise<CollectionEnvelope<IvrMenu>> {
    return this.api.request('/routing/ivr-menus', signal ? { signal } : {});
  }

  menu(id: string): Promise<DataEnvelope<IvrMenu>> {
    return this.api.request(`/routing/ivr-menus/${seg(id)}`);
  }

  /** 201; 409 `ivr_menu_name_conflict`; 422 with a target, prompt, cycle or depth code. */
  createMenu(body: CreateIvrMenu): Promise<DataEnvelope<IvrMenu>> {
    return this.api.send('POST', '/routing/ivr-menus', body);
  }

  updateMenu(id: string, body: UpdateIvrMenu): Promise<DataEnvelope<IvrMenu>> {
    return this.api.send('PATCH', `/routing/ivr-menus/${seg(id)}`, body);
  }

  /** 204; 409 `ivr_menu_in_use` while a rule or another menu names it, or `version_conflict`. */
  deleteMenu(id: string, expectedVersion: number): Promise<void> {
    return this.api.send('DELETE', `/routing/ivr-menus/${seg(id)}${query({ expected_version: expectedVersion })}`);
  }
}

// ---- Blocked and do-not-call list, and call notes (B6; docs/api/BLOCKLIST.md) ------------------------
// Seam: narrow local copies of the pinned B6 contract while packages/contracts lands its blocklist and
// call-note schemas. Swap these for the contract types once they settle. B6 answers these routes
// without the {data, meta} envelope; features/blocklist/model.ts and features/inbox/notes.ts accept
// either shape and re-validate every response before display.

export type BlocklistList = 'calls_inbound' | 'calls_outbound' | 'texts';
export type BlocklistSource = 'manual' | 'csv' | 'stop_reply' | 'carrier_complaint';
export interface BlocklistPerson {
  id: string | null;
  name: string;
}
export interface BlocklistEntry {
  id: string;
  e164: string;
  lists: BlocklistList[];
  reason: string;
  note: string;
  source: BlocklistSource;
  source_ref: string | null;
  added_by: BlocklistPerson;
  added_at: string;
  expires_at: string | null;
  updated_at: string;
}
export interface BlocklistQuery {
  query?: string | null;
  list?: BlocklistList | null;
  cursor?: string | null;
  limit?: number;
}
export interface BlocklistCreate {
  e164: string;
  lists: BlocklistList[];
  reason: string;
  note?: string;
  expires_at?: string | null;
}
export type BlocklistPatch = Partial<Omit<BlocklistCreate, 'e164'>>;
/** One CSV row after the browser parsed and normalised it. Imports never carry an expiry. */
export interface BlocklistImportRow {
  e164: string;
  lists: BlocklistList[];
  reason: string;
  note?: string;
}
/** `row` is the 1-based position in the `rows` array that was sent, not the CSV line. */
export interface BlocklistInvalidRow {
  row: number;
  e164: string;
  error: string;
}
export interface BlocklistCounts {
  added: number;
  updated: number;
  unchanged: number;
  invalid: BlocklistInvalidRow[];
}
export interface BlocklistHistoryEvent {
  at: string;
  actor: BlocklistPerson | null;
  event: string;
  detail: unknown;
}
export interface BlocklistImportRecord {
  id: string;
  filename: string;
  by: BlocklistPerson | null;
  at: string;
  counts: { added: number; updated: number; unchanged: number; invalid: number };
}
export interface CallNote {
  id: string;
  body: string;
  author: { id: string; name: string };
  created_at: string;
  updated_at: string;
}

/** Typed calls for /blocklist/**. Responses are returned raw for the feature's readers. Never retries a change. */
export class BlocklistClient {
  constructor(private readonly api: Pick<ApiClient, 'request' | 'send'>) {}

  /** `{items, next_cursor}`; `query` matches the number or the reason. */
  list(q: BlocklistQuery = {}, signal?: AbortSignal): Promise<unknown> {
    return this.api.request(`/blocklist${query({ query: q.query, list: q.list, cursor: q.cursor, limit: q.limit ?? 50 })}`, signal ? { signal } : {});
  }

  /** 201 entry; 409 `blocklist_exists` when the number is already listed (update it instead). */
  create(body: BlocklistCreate): Promise<unknown> {
    return this.api.send('POST', '/blocklist', body);
  }

  update(id: string, body: BlocklistPatch): Promise<unknown> {
    return this.api.send('PATCH', `/blocklist/${seg(id)}`, body);
  }

  /** 204. The entry's history keeps a `removed` event; a texts opt-out stays in place. */
  remove(id: string): Promise<void> {
    return this.api.send('DELETE', `/blocklist/${seg(id)}`);
  }

  history(id: string, signal?: AbortSignal): Promise<unknown> {
    return this.api.request(`/blocklist/${seg(id)}/history`, signal ? { signal } : {});
  }

  /** `{blocked, entry}` for one E.164 number. */
  lookup(e164: string, signal?: AbortSignal): Promise<unknown> {
    return this.api.request(`/blocklist/lookup${query({ e164 })}`, signal ? { signal } : {});
  }

  /** Dry run: `{preview}`. Real import: `{result, import_id}`. At most 5,000 rows; rate limited. */
  importRows(filename: string, rows: BlocklistImportRow[], dryRun: boolean): Promise<unknown> {
    return this.api.send('POST', '/blocklist/imports', { filename, dry_run: dryRun, rows });
  }

  imports(signal?: AbortSignal): Promise<unknown> {
    return this.api.request('/blocklist/imports', signal ? { signal } : {});
  }
}

/** Notes on a call; they follow the call's visibility. Authors may change their own note for 15 minutes. */
export class CallNotesClient {
  constructor(private readonly api: Pick<ApiClient, 'request' | 'send'>) {}

  list(callId: string, signal?: AbortSignal): Promise<unknown> {
    return this.api.request(`/calls/${seg(callId)}/notes`, signal ? { signal } : {});
  }

  /** 201 note. */
  add(callId: string, body: string): Promise<unknown> {
    return this.api.send('POST', `/calls/${seg(callId)}/notes`, { body });
  }

  /** 403 `permission_denied` for someone else's note; 409 `note_edit_window_closed` after 15 minutes. */
  edit(callId: string, noteId: string, body: string): Promise<unknown> {
    return this.api.send('PATCH', `/calls/${seg(callId)}/notes/${seg(noteId)}`, { body });
  }

  remove(callId: string, noteId: string): Promise<void> {
    return this.api.send('DELETE', `/calls/${seg(callId)}/notes/${seg(noteId)}`);
  }
}

// ---- Google sign-in and people administration (B8; docs/api/AUTH-GOOGLE.md) --------------------------
// Responses are returned raw: identity/google.ts and identity/people.ts accept the bare or {data}
// shape and re-validate before display. The browser never talks to Google itself.

/** PATCH /admin/sso: only the fields that change. Super-admin emails are read-only and never sent. */
export interface SsoPolicyUpdate {
  google_enabled?: boolean;
  allowed_domains?: string[];
  auto_join_role_id?: string;
}

/** PATCH /admin/users/{id}. The server refuses to demote or deactivate a super admin. */
export interface PersonUpdate {
  role_id?: string;
  active?: boolean;
}

/** Typed calls for /auth/providers, the Google start route, /admin/sso and /admin/users. Never retries a change. */
export class SsoClient {
  constructor(private readonly api: Pick<ApiClient, 'request' | 'send' | 'url'>) {}

  /** Public. `{google: {enabled, allowed_domains}}`. */
  providers(signal?: AbortSignal): Promise<unknown> {
    return this.api.request('/auth/providers', signal ? { signal } : {});
  }

  /** Same-origin URL for a plain link: the API redirects to Google and back, then on to `returnTo`. */
  googleStartUrl(returnTo: string): string {
    return this.api.url(`/auth/google/start${query({ return_to: returnTo })}`);
  }

  policy(signal?: AbortSignal): Promise<unknown> {
    return this.api.request('/admin/sso', signal ? { signal } : {});
  }

  savePolicy(body: SsoPolicyUpdate): Promise<unknown> {
    return this.api.send('PATCH', '/admin/sso', body);
  }

  updatePerson(userId: string, body: PersonUpdate): Promise<unknown> {
    return this.api.send('PATCH', `/admin/users/${seg(userId)}`, body);
  }
}

// ---- Voicemail drops (B11, round 13; contract: .fleet briefs/B11.md "Design (pinned)") --------------------
// packages/contracts/src/voicemail-drops.ts has the schemas. Responses are returned raw;
// features/voicemail/drops.ts re-validates every row and option before display. Libraries are keyed to the
// line: a workspace number's drops (`scope=number`) and a person's direct-line drops (`scope=user_line`).

export type VoicemailDropScope = 'number' | 'user_line';

/** Which library: one workspace number's, or one person's direct line. */
export type VoicemailDropOwner = { scope: 'number'; numberId: string } | { scope: 'user_line'; userId: string };

/** PATCH /voicemail-drops/{id} (UpdateVoicemailDropSchema): only what changes, against the drop's version. */
export interface VoicemailDropPatch {
  name?: string;
  sort_order?: number;
  expected_version: number;
}

const ownerQuery = (owner: VoicemailDropOwner) => (owner.scope === 'number' ? { scope: owner.scope, number_id: owner.numberId } : { scope: owner.scope, user_id: owner.userId });

/** Typed calls for /voicemail-drops/**, a call's drop options and the drop command. Never retries a change. */
export class VoicemailDropsClient {
  constructor(private readonly api: Pick<ApiClient, 'request' | 'send' | 'open'>) {}

  /** One library, in its sort order. */
  list(owner: VoicemailDropOwner, signal?: AbortSignal): Promise<unknown> {
    return this.api.request(`/voicemail-drops${query(ownerQuery(owner))}`, signal ? { signal } : {});
  }

  /** Raw canonical WAV through the greeting path's checks (201). 409 at the library's limit or for a taken name; 413, 415 and 422 carry the server's reason. */
  create(owner: VoicemailDropOwner, name: string, wav: Uint8Array<ArrayBuffer>): Promise<unknown> {
    return this.api.request(`/voicemail-drops${query({ ...ownerQuery(owner), name })}`, { method: 'POST', body: wav, headers: { 'Content-Type': 'audio/wav' } });
  }

  /** Rename or move; 409 `version_conflict` when stale. */
  update(id: string, patch: VoicemailDropPatch): Promise<unknown> {
    return this.api.send('PATCH', `/voicemail-drops/${seg(id)}`, patch);
  }

  /** 204. Deleting the default makes the next drop in the library the default (server rule). */
  remove(id: string): Promise<void> {
    return this.api.send('DELETE', `/voicemail-drops/${seg(id)}`);
  }

  /** Makes this drop its library's one-tap default. */
  makeDefault(id: string): Promise<unknown> {
    return this.api.send('POST', `/voicemail-drops/${seg(id)}/default`, {});
  }

  /**
   * The saved audio, for the inline preview (B11-r2 added the route: canonical WAV, private, no-store). Same
   * credentials and error envelope as other API calls; on an API without it, 404/405 reads as "not on this server".
   */
  audio(id: string, signal?: AbortSignal): Promise<Response> {
    return this.api.open(`/voicemail-drops/${seg(id)}/audio`, signal ? { signal } : {});
  }

  /** The drops a call can leave, in the contract's order: the calling number's, then the agent's own. */
  options(callId: string, signal?: AbortSignal): Promise<unknown> {
    return this.api.request(`/calls/${seg(callId)}/voicemail-drop-options`, signal ? { signal } : {});
  }

  /** The `voicemail_drop` call action: one per call. The same key re-sent returns the original receipt. */
  drop(callId: string, dropId: string, expectedVersion: number, idempotencyKey: string): Promise<DataEnvelope<unknown>> {
    return this.api.send('POST', `/calls/${seg(callId)}/actions`, { action: 'voicemail_drop', drop_id: dropId, expected_version: expectedVersion }, { idempotencyKey });
  }

  /** A command's latest receipt, while the drop is accepted but not yet applied. */
  command(commandId: string): Promise<DataEnvelope<unknown>> {
    return this.api.request(`/commands/${seg(commandId)}`);
  }
}
